// src/hooks/useDemoPlan.tsx
// ============================================================
// DEMO MODE PLAN HOOK
// 
// Wraps the real usePlan() hook but allows localStorage override
// for smooth demo presentations.
//
// Usage:
//   const { plan, isPro, isFreemium, upgrade, reset } = useDemoPlan();
//
// Demo Flow:
//   1. Start in Freemium (or whatever real plan is)
//   2. Call upgrade() → instantly switches to Pro
//   3. Press Ctrl+Shift+F → resets to real plan
//
// The override persists in localStorage so it survives page refreshes
// during demos, but can be cleared with reset() or keyboard shortcut.
//
// IMPORTANT: Uses custom events to sync state across all hook instances!
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { usePlan } from '../utils/planUtils';

const DEMO_PLAN_KEY = 'dva_demo_plan_override';
const DEMO_PLAN_CHANGE_EVENT = 'dva_demo_plan_change';

export type DemoPlanType = 'freemium' | 'pro';

interface UseDemoPlanReturn {
  /** Current effective plan ('freemium' or 'pro') */
  plan: DemoPlanType;
  /** True if current plan is Pro */
  isPro: boolean;
  /** True if current plan is Freemium */
  isFreemium: boolean;
  /** True while loading plan data */
  isLoading: boolean;
  /** The real plan from entitlement (ignoring demo override) */
  realPlan: DemoPlanType;
  /** True if demo override is active */
  isDemoOverride: boolean;
  /** Instantly upgrade to Pro (for demo) */
  upgrade: () => void;
  /** Reset to real plan (clears demo override) */
  reset: () => void;
  /** Set a specific plan override */
  setDemoPlan: (plan: DemoPlanType) => void;
}

// Helper to dispatch plan change event (syncs all hook instances)
function dispatchPlanChange(plan: DemoPlanType | null) {
  window.dispatchEvent(new CustomEvent(DEMO_PLAN_CHANGE_EVENT, { detail: plan }));
}

export function useDemoPlan(): UseDemoPlanReturn {
  const { plan: realPlan, isLoading } = usePlan();
  
  // Initialize from localStorage synchronously to avoid flash
  const [demoOverride, setDemoOverride] = useState<DemoPlanType | null>(() => {
    if (typeof window === 'undefined') return null;
    const stored = localStorage.getItem(DEMO_PLAN_KEY);
    if (stored === 'freemium' || stored === 'pro') {
      return stored;
    }
    return null;
  });

  // Listen for plan changes from OTHER hook instances (via custom event)
  useEffect(() => {
    const handlePlanChange = (e: CustomEvent<DemoPlanType | null>) => {
      console.log('📡 useDemoPlan received plan change event:', e.detail);
      setDemoOverride(e.detail);
    };

    window.addEventListener(DEMO_PLAN_CHANGE_EVENT, handlePlanChange as EventListener);
    return () => {
      window.removeEventListener(DEMO_PLAN_CHANGE_EVENT, handlePlanChange as EventListener);
    };
  }, []);

  // Keyboard shortcut: Ctrl+Shift+D (Windows) or Cmd+Shift+D (Mac) to reset demo mode
  // Also supports pressing Escape 3 times quickly
  useEffect(() => {
    let escapeCount = 0;
    let escapeTimer: NodeJS.Timeout | null = null;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Option 1: Ctrl/Cmd + Shift + D
      const modifierKey = e.ctrlKey || e.metaKey;
      if (modifierKey && e.shiftKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        localStorage.removeItem(DEMO_PLAN_KEY);
        setDemoOverride(null);
        dispatchPlanChange(null);
        console.log('🔄 Demo mode reset - using real plan:', realPlan);
        return;
      }

      // Option 2: Triple-press Escape
      if (e.key === 'Escape') {
        escapeCount++;
        if (escapeTimer) clearTimeout(escapeTimer);
        
        if (escapeCount >= 3) {
          escapeCount = 0;
          localStorage.removeItem(DEMO_PLAN_KEY);
          setDemoOverride(null);
          dispatchPlanChange(null);
          console.log('🔄 Demo mode reset (Escape x3) - using real plan:', realPlan);
        } else {
          escapeTimer = setTimeout(() => {
            escapeCount = 0;
          }, 1000); // Reset count after 1 second
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (escapeTimer) clearTimeout(escapeTimer);
    };
  }, [realPlan]);

  // Effective plan (demo override takes precedence)
  const effectivePlan = demoOverride || realPlan;

  // Upgrade to Pro
  const upgrade = useCallback(() => {
    localStorage.setItem(DEMO_PLAN_KEY, 'pro');
    setDemoOverride('pro');
    dispatchPlanChange('pro');
    console.log('⬆️ Demo upgraded to Pro');
  }, []);

  // Reset to real plan
  const reset = useCallback(() => {
    localStorage.removeItem(DEMO_PLAN_KEY);
    setDemoOverride(null);
    dispatchPlanChange(null);
    console.log('🔄 Demo mode reset - using real plan:', realPlan);
  }, [realPlan]);

  // Set specific demo plan
  const setDemoPlan = useCallback((plan: DemoPlanType) => {
    localStorage.setItem(DEMO_PLAN_KEY, plan);
    setDemoOverride(plan);
    dispatchPlanChange(plan);
    console.log('🎭 Demo plan set to:', plan);
  }, []);

  return {
    plan: effectivePlan,
    isPro: effectivePlan === 'pro',
    isFreemium: effectivePlan === 'freemium',
    isLoading,
    realPlan,
    isDemoOverride: demoOverride !== null,
    upgrade,
    reset,
    setDemoPlan,
  };
}

// ============================================================
// FLOATING DEMO TOGGLE COMPONENT
// Shows current plan and allows quick switching during demos
// Only visible in development mode
// ============================================================
export function DemoModeIndicator() {
  const { plan, isPro, isFreemium, isDemoOverride, upgrade, reset, setDemoPlan } = useDemoPlan();
  const [isExpanded, setIsExpanded] = useState(false);

  // Only show in development
  const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';
  if (!isDev) return null;

  return (
    <div className="fixed bottom-4 left-4 z-50">
      {/* Collapsed indicator */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg text-xs font-medium
          transition-all duration-200
          ${isPro 
            ? 'bg-emerald-500 text-white hover:bg-emerald-600' 
            : 'bg-amber-500 text-white hover:bg-amber-600'
          }
        `}
      >
        <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
        {isPro ? 'PRO' : 'FREE'}
        {isDemoOverride && <span className="opacity-75">(demo)</span>}
      </button>

      {/* Expanded panel */}
      {isExpanded && (
        <div className="absolute bottom-12 left-0 bg-white rounded-lg shadow-xl border border-gray-200 p-3 min-w-[200px]">
          <div className="text-xs text-gray-500 mb-2">Demo Mode Controls</div>
          
          <div className="flex flex-col gap-2">
            <button
              onClick={() => setDemoPlan('freemium')}
              className={`
                px-3 py-1.5 rounded text-xs font-medium transition-all
                ${isFreemium 
                  ? 'bg-amber-500 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }
              `}
            >
              Freemium
            </button>
            
            <button
              onClick={() => setDemoPlan('pro')}
              className={`
                px-3 py-1.5 rounded text-xs font-medium transition-all
                ${isPro 
                  ? 'bg-emerald-500 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }
              `}
            >
              Pro
            </button>

            {isDemoOverride && (
              <button
                onClick={reset}
                className="px-3 py-1.5 rounded text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
              >
                Reset to Real Plan
              </button>
            )}
          </div>
          
          <div className="mt-2 pt-2 border-t border-gray-100 text-[10px] text-gray-400">
            Ctrl+Shift+F to reset
          </div>
        </div>
      )}
    </div>
  );
}